/**
 * TeacherApprovalsTab – students waiting to join one of the teacher's classes.
 *
 * Only students who asked for a class this teacher teaches are listed (the
 * server enforces the same). Approving places the student in that class in the
 * same step, and says so when the class is full.
 */

import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Check, Loader2, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { DataTable, type Column } from '../analytics/DataTable';
import { AnalyticsEmptyState, AnalyticsPanel, TableSkeleton } from '../analytics/AnalyticsPanel';
import type { RosterPerson, SchoolRoster } from '../../hooks/useSchoolRoster';
import { decideStudent } from '../../services/schoolRosterService';
import { MAX_CLASS_SIZE } from '../../services/classManagementService';
import { pendingForTeacher, requestedClassOf } from '../../lib/classroom/students';
import { classLabel } from './ClassDrawer';

export function TeacherApprovalsTab({ roster, myClassIds }: { roster: SchoolRoster; myClassIds: string[] }) {
  const pending = useMemo(() => pendingForTeacher(roster.students, myClassIds), [roster.students, myClassIds]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<string[] | null>(null);
  const [reason, setReason] = useState('');

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  /** One request per student: each is placed in the class they asked for. */
  const decide = async (ids: string[], decision: 'approve' | 'reject') => {
    setBusy(decision);
    const failures: string[] = [];
    let done = 0;
    try {
      for (const id of ids) {
        const r = await decideStudent(id, decision, decision === 'reject' ? { reason: reason.trim() || undefined } : {});
        if (r.ok) done += 1;
        else failures.push(`${roster.students.find((s) => s.id === id)?.name ?? 'A student'}: ${r.message}`);
      }
    } finally {
      setBusy(null);
    }
    if (done) toast.success(`${done} student${done === 1 ? '' : 's'} ${decision === 'approve' ? 'approved and placed' : 'rejected'}`);
    if (failures.length) toast.error(failures.join('; '));
    setSelected(new Set());
    setRejecting(null);
    setReason('');
  };

  const classOf = (s: RosterPerson) => {
    const id = requestedClassOf(s);
    return id ? roster.classById.get(id) ?? null : null;
  };

  const columns: Column<RosterPerson>[] = [
    {
      key: 'name',
      header: 'Student',
      sortValue: (s) => s.name,
      cell: (s) => (
        <div className="flex min-w-0 items-center gap-3">
          <input
            type="checkbox"
            className="h-4 w-4 shrink-0 accent-primary"
            checked={selected.has(s.id)}
            onChange={() => toggle(s.id)}
            onClick={(e) => e.stopPropagation()}
            aria-label={`Select ${s.name}`}
          />
          <div className="min-w-0">
            <p className="truncate font-medium text-foreground">{s.name}</p>
            <p className="truncate text-xs text-muted-foreground">{s.email}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'class',
      header: 'Asked for',
      sortValue: (s) => (classOf(s) ? classLabel(classOf(s)!) : null),
      cell: (s) => {
        const c = classOf(s);
        if (!c) return <span className="text-muted-foreground">—</span>;
        const count = (c.student_ids ?? []).length;
        return (
          <div>
            <p className="text-foreground">{classLabel(c)}</p>
            <p className={`text-[11px] ${count >= MAX_CLASS_SIZE ? 'text-destructive' : 'text-muted-foreground'}`}>
              {count >= MAX_CLASS_SIZE ? 'Class is full' : `${count}/${MAX_CLASS_SIZE} students`}
            </p>
          </div>
        );
      },
    },
    {
      key: 'joined',
      header: 'Signed up',
      align: 'right',
      hideOnMobile: true,
      sortValue: (s) => s.createdAt,
      cell: (s) => <span className="tabular-nums text-muted-foreground">{s.createdAt ? new Date(s.createdAt).toLocaleDateString() : '—'}</span>,
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      cell: (s) => (
        <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
          <Button size="sm" variant="ghost" className="h-8 text-destructive" disabled={!!busy} onClick={() => setRejecting([s.id])} aria-label={`Reject ${s.name}`}>
            <X className="h-4 w-4" />
          </Button>
          <Button size="sm" className="h-8" disabled={!!busy} onClick={() => decide([s.id], 'approve')}>
            <Check className="mr-1 h-4 w-4" /> Approve
          </Button>
        </div>
      ),
    },
  ];

  return (
    <AnalyticsPanel
      title="Student approvals"
      description="Students who asked to join one of your classes. Approving places them in that class."
    >
      {roster.loading ? (
        <TableSkeleton />
      ) : (
        <>
          {(selected.size > 0 || rejecting) && (
            <div className="mb-3 space-y-2 rounded-lg border border-primary/30 bg-primary/5 p-2 text-sm">
              {rejecting ? (
                <>
                  <p className="px-1 font-medium text-foreground">
                    Reject {rejecting.length === 1 ? roster.students.find((s) => s.id === rejecting[0])?.name : `${rejecting.length} students`}?
                  </p>
                  <Textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Reason (optional, kept on record)"
                    className="min-h-[56px] text-sm"
                    maxLength={500}
                    disabled={!!busy}
                  />
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="ghost" onClick={() => setRejecting(null)} disabled={!!busy}>
                      Cancel
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => decide(rejecting, 'reject')} disabled={!!busy}>
                      {busy === 'reject' && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                      Reject
                    </Button>
                  </div>
                </>
              ) : (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="px-1 text-foreground">{selected.size} selected</span>
                  <Button size="sm" onClick={() => decide([...selected], 'approve')} disabled={!!busy}>
                    {busy === 'approve' && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                    Approve & place
                  </Button>
                  <Button size="sm" variant="outline" className="text-destructive" onClick={() => setRejecting([...selected])} disabled={!!busy}>
                    Reject
                  </Button>
                  <button type="button" className="ml-auto px-1 text-xs text-muted-foreground hover:text-foreground" onClick={() => setSelected(new Set())}>
                    Clear
                  </button>
                </div>
              )}
            </div>
          )}
          <DataTable
            rows={pending}
            columns={columns}
            rowKey={(s) => s.id}
            initialSort={{ key: 'joined', direction: 'asc' }}
            searchPlaceholder="Search name or email"
            searchText={(s) => `${s.name} ${s.email}`}
            onRowClick={(s) => toggle(s.id)}
            toolbar={
              pending.length > 1 ? (
                <button
                  type="button"
                  className="text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => setSelected(selected.size === pending.length ? new Set() : new Set(pending.map((s) => s.id)))}
                >
                  {selected.size === pending.length ? 'Clear selection' : `Select all ${pending.length}`}
                </button>
              ) : undefined
            }
            empty={
              <AnalyticsEmptyState
                compact
                title="No students waiting"
                description="Students who choose one of your classes when they sign up appear here for you to approve."
              />
            }
          />
        </>
      )}
    </AnalyticsPanel>
  );
}
